/**
 * GA4 Custom Event Helpers
 *
 * Fires custom events via gtag() for the Aviado Research Hub.
 * Measurement ID: G-LLM78E2TZV
 */

// Extend Window to include gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function fireEvent(
  eventName: string,
  params: Record<string, string | number>,
  eventCallback?: () => void,
) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventCallback ? { ...params, event_callback: eventCallback } : params);
  } else if (eventCallback) {
    // gtag unavailable (blocked / not yet loaded) — don't strand a caller
    // that's waiting on the callback to navigate.
    eventCallback();
  }
}

/**
 * Fires when a research article page loads.
 */
export function trackArticleRead(params: {
  article_slug: string;
  health_goal: string;
  supplement_name: string;
}) {
  fireEvent('article_read', params);
}

/**
 * Fires when a user views a product detail or AviScore.
 */
export function trackProductViewed(params: {
  product_id: string;
  product_name: string;
  article_referrer: string;
}) {
  fireEvent('product_viewed', params);
}

/**
 * Fires on affiliate/partner/purchase outbound link click.
 */
export function trackOutboundClick(params: {
  product_id: string;
  destination_url: string;
  article_referrer: string;
}) {
  fireEvent('outbound_click', params);
}

/**
 * Fires when a search is performed in the research library.
 */
export function trackSearchPerformed(params: {
  search_query: string;
  results_count: number;
}) {
  fireEvent('search_performed', params);
}

// ---------------------------------------------------------------------------
// Research CTA module events (see lib/cta.ts + components/cta/ResearchCTA.tsx)
// All carry { treatment, context, variant } so experiment arms are measurable.
// ---------------------------------------------------------------------------

interface CtaArmParams {
  treatment: string;
  context: string;
  variant: string;
}

/** CTA impression — fired once at ≥50% visibility. The GA4 denominator for
 * click-through rate per treatment/variant (mirrors the PostHog `cta_viewed`). */
export function trackCtaViewed(params: CtaArmParams) {
  fireEvent('cta_viewed', { ...params });
}

/** Primary beta-signup click ("Join the beta", etc). Fire-only — use for
 * modified/middle clicks (new tab/window), where the page does NOT unload so
 * the GA4 beacon sends normally. */
export function trackBetaSignupClick(params: CtaArmParams) {
  fireEvent('beta_signup_click', { ...params });
}

/**
 * Beta-signup click that reliably records BEFORE a cross-page navigation.
 *
 * The CTA button links to https://www.aviado.com/get-app (a different page/
 * project), so a plain same-tab click unloads this page immediately — which can
 * drop the GA4 hit before it's sent (this is why the event read 0). Fires
 * `beta_signup_click`, then navigates only once gtag confirms dispatch
 * (`event_callback`), with a 300ms fallback so a blocked/slow gtag never traps
 * the user. This is what makes the event safe to import into Google Ads.
 *
 * Call ONLY for same-tab left-clicks; for modified/middle clicks use
 * `trackBetaSignupClick` and let the browser open the new tab.
 */
export function trackBetaSignupClickThenNavigate(params: CtaArmParams, href: string) {
  let navigated = false;
  const go = () => {
    if (navigated) return;
    navigated = true;
    window.location.assign(href);
  };
  fireEvent('beta_signup_click', { ...params }, go);
  window.setTimeout(go, 300); // guaranteed navigation even if the callback never fires
}

/** Newsletter form submitted (any validity). */
export function trackNewsletterSubmit(params: CtaArmParams & { valid: boolean }) {
  fireEvent('newsletter_submit', { ...params, valid: String(params.valid) });
}

/** Newsletter subscribe succeeded. */
export function trackNewsletterSuccess(params: CtaArmParams & { list: string }) {
  fireEvent('newsletter_success', { ...params });
}

/** Newsletter validation or network failure. */
export function trackNewsletterError(params: CtaArmParams & { reason: string }) {
  fireEvent('newsletter_error', { ...params });
}
